class PredictiveSearchForm {
  constructor() {
    this.form = document.querySelector('#predictive-search-form');
    this.input = this.form?.querySelector('input[type="search"]');
    this.clearButton = this.form?.querySelector('.clear-search');
    this.isLoading = false;
    this.cache = new Map();
    this.selectedResultIndex = -1;

    if (this.input) {
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', 'predictive-search-results');
      this.input.setAttribute('aria-owns', 'predictive-search-results');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-label', 'Search products');
    }

    if (this.clearButton) {
      this.clearButton.style.display = 'none';
      this.clearButton.setAttribute('aria-label', 'Clear search');
    }

    this.bindEvents();
  }

  bindEvents() {
    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.debounce(this.handleChange.bind(this), 300);
    this.onClear = this.onClear.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.form?.addEventListener('submit', this.onSubmit);
    this.input?.addEventListener('input', this.onChange);
    this.input?.addEventListener('keydown', this.onKeydown);
    this.clearButton?.addEventListener('click', this.onClear);
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        document.dispatchEvent(
          new CustomEvent('predictive-search:navigate', {
            detail: { direction }
          })
        );
        break;
      case 'Escape':
        event.preventDefault();
        this.onClear();
        break;
    }
  }

  async handleChange(event) {
    const searchTerm = event.target.value.trim();
    
    if (this.clearButton) {
      this.clearButton.style.display = searchTerm.length > 0 ? 'block' : 'none';
    }
    
    this.input?.setAttribute('aria-expanded', searchTerm.length >= 2 ? 'true' : 'false');
    
    if (searchTerm.length < 2) {
      this.emitResults({ products: [], searchTerm: '', showResults: false });
      document.dispatchEvent(new CustomEvent('predictive-search:clear'));
      return;
    }
  
    if (this.cache.has(searchTerm)) {
      const cached = this.cache.get(searchTerm);
      this.emitResults({ ...cached, searchTerm, showResults: true });
      return;
    }
  
    this.setLoading(true);
    await this.getSearchResults(searchTerm);
  }

  async getSearchResults(searchTerm) {
    try {
      const params = new URLSearchParams({
        'q': searchTerm,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,vendor,product_type,variants.title,variants.sku,variants.barcode,tag,body'
      });

      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest.json?${params.toString()}`
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Search failed');
      }
      
      const data = await response.json();
      const results = {
        products: data?.resources?.results?.products || [],
        searchTerm
      };
      
      this.cache.set(searchTerm, results);
      this.emitResults(results);
    } catch (error) {
      console.error('Search error:', error);
      this.emitError(error);
    } finally {
      this.setLoading(false);
    }
  }

  emitResults(results) {
    document.dispatchEvent(
      new CustomEvent('predictive-search:results', {
        detail: { 
          results,
          searchTerm: results.searchTerm || this.input?.value.trim() || ''
        }
      })
    );
  }

  emitError(error) {
    document.dispatchEvent(
      new CustomEvent('predictive-search:error', {
        detail: { 
          message: error.message || 'Search failed',
          code: error.status || 500
        }
      })
    );
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    document.dispatchEvent(
      new CustomEvent('predictive-search:loading', {
        detail: { isLoading }
      })
    );
  }

  onSubmit(event) {
    event.preventDefault();
    const searchTerm = this.input?.value.trim();
    
    if (searchTerm) {
      const searchParams = new URLSearchParams({ q: searchTerm });
      window.location.href = `/search?${searchParams.toString()}`;
    }
  }

  onClear() {
    if (this.input) {
      this.input.value = '';
      this.input.focus();
      this.input.setAttribute('aria-expanded', 'false');
      document.dispatchEvent(
        new CustomEvent('predictive-search:clear')
      );
    }
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

class PredictiveSearchResults {
  constructor() {
    this.resultsContainer = document.querySelector('#predictive-search-results');
    this.recommendationsSection = document.querySelector('#predictive-search-recommendations');
    this.featuredLinksSection = document.querySelector('#featured-links');
    this.loadingClass = 'is-loading';
    this.selectedIndex = -1;
    
    if (this.resultsContainer) {
      this.resultsContainer.setAttribute('role', 'listbox');
      this.resultsContainer.setAttribute('aria-label', 'Search results');
    }

    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('predictive-search:results', this.onResultsReceived.bind(this));
    document.addEventListener('predictive-search:error', this.onError.bind(this));
    document.addEventListener('predictive-search:loading', this.onLoadingChange.bind(this));
    document.addEventListener('predictive-search:clear', this.onSearchClear.bind(this));
    document.addEventListener('predictive-search:navigate', this.onNavigate.bind(this));
  }

  onNavigate(event) {
    const { direction } = event.detail;
    const items = this.resultsContainer?.querySelectorAll('[role="option"]') || [];
    
    if (!items.length) return;

    this.selectedIndex = Math.max(0, Math.min(
      items.length - 1,
      this.selectedIndex + direction
    ));

    items.forEach((item, index) => {
      item.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false');
      if (index === this.selectedIndex) {
        item.focus();
      }
    });
  }

  showSections() {
    if (this.recommendationsSection) {
      this.recommendationsSection.classList.remove('hidden');
    }
    if (this.featuredLinksSection) {
      this.featuredLinksSection.classList.remove('hidden');
    }
    if (this.resultsContainer) {
      this.resultsContainer.classList.add('hidden');
    }
  }

  hideSections() {
    if (this.recommendationsSection) {
      this.recommendationsSection.classList.add('hidden');
    }
    if (this.featuredLinksSection) {
      this.featuredLinksSection.classList.add('hidden');
    }
  }

  onSearchClear() {
    this.showSections();
    this.selectedIndex = -1;
  }

  onResultsReceived(event) {
    const { results, searchTerm } = event.detail;
    const products = results?.products || [];
    
    if (this.resultsContainer) {
      this.resultsContainer.classList.remove('hidden');
    }
    this.hideSections();
    
    this.render(products, searchTerm);
  }

  onError(event) {
    const message = event.detail?.message || 'Something went wrong';
    this.renderError(message);
    this.showSections();
  }

  onLoadingChange(event) {
    const { isLoading } = event.detail;
    if (this.resultsContainer) {
      this.resultsContainer.classList.toggle(this.loadingClass, isLoading);
      this.resultsContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  renderError(message) {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = `
        <div class="search-error" role="alert">
          <p>${message}</p>
        </div>
      `;
    }
  }

  render(products, searchTerm) {
    if (!this.resultsContainer) return;

    if (!products.length) {
      this.resultsContainer.innerHTML = `
        <div class="no-results" role="status" aria-live="polite">
          <p>Your search for "${searchTerm}" didn't return any results</p>
        </div>
      `;
      this.hideSections();
      return;
    }

    this.resultsContainer.innerHTML = `
      <ul class="search-results-list" role="listbox">
        ${products.map((product, index) => `
          <li 
            class="search-result-item${!product.available ? ' sold-out' : ''}"
            role="option"
            id="search-result-${index}"
            aria-selected="false"
            tabindex="-1"
          >
            <a href="${product.url}" class="product-link" 
               aria-label="${product.title}${!product.available ? ' - Sold out' : ''}"
            >
              <div class="product-image-wrapper" aria-hidden="true">
                ${product.image ? `
                  <img 
                    src="${product.image}" 
                    alt="${product.title}"
                    class="product-image"
                    width="256"
                    height="256"
                    loading="lazy"
                  />
                ` : '<div class="product-image-placeholder"></div>'}
              </div>
              <div class="product-info">
                <h3 class="product-title">${product.title}</h3>
                <div class="product-meta">
                  ${product.compare_at_price_max > product.price ? `
                    <div class="price on-sale">
                      <span class="current-price theme-money">$${product.price}</span>
                      <span class="was-price theme-money">$${product.compare_at_price_max}</span>
                    </div>
                  ` : `
                    <span class="product-price">$${product.price}</span>
                  `}
                </div>
              </div>
            </a>
          </li>
        `).join('')}
      </ul>
      ${searchTerm ? `
        <div class="search-footer">
          <a href="/search?q=${encodeURIComponent(searchTerm)}" 
             class="view-all-results"
             role="button"
             aria-label="See all results for ${searchTerm}"
          >
            See all results for "${searchTerm}"
          </a>
        </div>
      ` : ''}
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new PredictiveSearchForm();
  new PredictiveSearchResults();
});